import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import LoadingScreen from '@/components/common/LoadingScreen';
import { useEffect, useState } from 'react';
import { debugLogger } from '@/utils/debugLogger';
import { pLogger } from '@/utils/persistentLogger';

export default function AuthGuard() {
  const store = useAuthStore();
  const { accessToken, _hasHydrated, hydrate } = store;
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    pLogger.log('AUTHGUARD', `Checking auth: ${location.pathname}`);

    // Check auth immediately - no delay needed since we use manual localStorage management
    const checkAuth = () => {
      // ALWAYS check localStorage first before checking store
      const localToken = localStorage.getItem('access_token');
      const localUserInfo = localStorage.getItem('user_info');

      pLogger.log('AUTHGUARD', `localStorage - token: ${localToken ? 'EXISTS(' + localToken.length + ')' : 'NULL'}, userInfo: ${localUserInfo ? 'EXISTS' : 'NULL'}`);
      pLogger.log('AUTHGUARD', `Store - token: ${accessToken ? 'EXISTS(' + accessToken.length + ')' : 'NULL'}`);

      // If localStorage has auth but store doesn't, hydrate first
      if (localToken && localUserInfo && !accessToken) {
        pLogger.log('AUTHGUARD', 'localStorage has auth data but store does not; hydrating...');
        hydrate();

        // Re-check store after hydration
        const storeAfterHydrate = useAuthStore.getState();
        pLogger.log('AUTHGUARD', `After hydrate: token=${!!storeAfterHydrate.accessToken}, user=${!!storeAfterHydrate.userInfo}`);

        if (storeAfterHydrate.accessToken) {
          pLogger.log('AUTHGUARD', '✅ Auth check passed after hydrate');
          setIsChecking(false);
          return;
        }
      }

      // If we have token (either in store or after hydration)
      if (accessToken || localToken) {
        pLogger.log('AUTHGUARD', `✅ Auth check passed - token length: ${(accessToken || localToken)?.length}`);
        setIsChecking(false);
        return;
      }

      // No auth in either store or localStorage
      pLogger.warn('AUTHGUARD', '❌ No auth info found, redirecting to /login');
      setIsChecking(false);
    };

    checkAuth();
  }, [accessToken, location.pathname, hydrate, _hasHydrated]);

  // Show loading while checking
  if (isChecking) {
    debugLogger.log(`⏳ [Guards/AuthGuard] Checking auth...`);
    return <LoadingScreen />;
  }

  // Check if authenticated
  if (!accessToken) {
    debugLogger.log(`🔒 [Guards/AuthGuard] Not authenticated, redirecting to /login`);
    return <Navigate to="/login" replace />;
  }

  debugLogger.log(`✅ [Guards/AuthGuard] Authenticated, rendering protected route`);
  return <Outlet />;
}
